import { useEffect } from 'react';
import { locations } from '@/data/locations';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'product';
  structuredData?: object;
  noIndex?: boolean;
}

const BASE_URL = 'https://supremeturf.com.au';
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.jpg`;
const SITE_NAME = 'Supreme Turf';

export const SEO = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  structuredData,
  noIndex = false,
}: SEOProps) => {
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const fullCanonicalUrl = canonicalUrl
    ? `${BASE_URL}${canonicalUrl}`
    : currentPath
      ? `${BASE_URL}${currentPath}`
      : undefined;

  useEffect(() => {
    document.title = fullTitle;

    const updateMeta = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    const updateLink = (rel: string, href: string) => {
      let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        document.head.appendChild(link);
      }
      link.href = href;
    };

    updateMeta('description', description);
    if (keywords) updateMeta('keywords', keywords);

    if (noIndex) {
      updateMeta('robots', 'noindex, nofollow');
    } else {
      updateMeta('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    }

    updateMeta('og:title', fullTitle, true);
    updateMeta('og:description', description, true);
    updateMeta('og:type', ogType, true);
    updateMeta('og:image', ogImage, true);
    updateMeta('og:site_name', SITE_NAME, true);
    updateMeta('og:locale', 'en_AU', true);
    if (fullCanonicalUrl) {
      updateMeta('og:url', fullCanonicalUrl, true);
    }

    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', fullTitle);
    updateMeta('twitter:description', description);
    updateMeta('twitter:image', ogImage);

    if (fullCanonicalUrl) {
      updateLink('canonical', fullCanonicalUrl);
    }

    const existingScript = document.querySelector('script[data-seo-schema]');
    if (existingScript) {
      existingScript.remove();
    }

    if (structuredData) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-schema', 'true');
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

    return () => {
      const schemaScript = document.querySelector('script[data-seo-schema]');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, [fullTitle, description, keywords, fullCanonicalUrl, ogImage, ogType, structuredData, noIndex]);

  return null;
};

// Organization Schema for homepage — canonical Supreme Turf entity.
// Uses stable @id so LocalBusiness / Product schemas can reference it.
export const getOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${BASE_URL}/#organization`,
  name: 'Supreme Turf',
  legalName: 'GOLDCOIN PACKAGING (AUSTRALIA) PTY. LTD.',
  description: 'Australian supplier of artificial grass, synthetic turf and complementary outdoor surface products, with warehouse pickup and delivery across QLD, NSW, VIC and SA.',
  url: BASE_URL,
  logo: {
    '@type': 'ImageObject',
    '@id': `${BASE_URL}/#logo`,
    url: `${BASE_URL}/apple-touch-icon.png`,
  },
  brand: { '@id': `${BASE_URL}/#brand` },
  contactPoint: [
    {
      '@type': 'ContactPoint',
      telephone: '+61-457-585-901',
      contactType: 'sales',
      areaServed: 'AU',
      availableLanguage: 'English',
    },
    {
      '@type': 'ContactPoint',
      telephone: '+61-450-954-913',
      contactType: 'sales',
      areaServed: 'AU',
      availableLanguage: 'English',
    },
  ],
  sameAs: [
    'https://www.facebook.com/supremeturf.au',
    'https://www.instagram.com/supreme.turf/',
    'https://www.tiktok.com/@supreme.turf',
    'https://youtube.com/@supremeturf',
    'https://www.linkedin.com/company/supremeturf/',
  ],
});

// Brand entity — separate from the Organization so Product > brand can reference it.
export const getBrandSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Brand',
  '@id': `${BASE_URL}/#brand`,
  name: 'Supreme Turf',
  logo: `${BASE_URL}/apple-touch-icon.png`,
});


// Local Business Schema — DEPRECATED for homepage use.
//
// Per-branch LocalBusiness entities live on the individual `/locations/:id`
// pages (see src/pages/locations/LocationPage.tsx), which read verified hours
// from src/data/locations.ts. The homepage advertises the Organization only —
// this avoids a monolithic LocalBusiness with invented shared hours (branches
// have different Saturday hours) and a duplicate/mismatched @id.
//
// Kept as an export purely to avoid breaking any imports until callers are
// migrated; returns null so JSON.stringify drops it from an @graph array.
export const getLocalBusinessSchema = () => null;

// Multi-branch LocalBusiness graph — used by /contact, which lists every branch.
// Each entity reuses the same @id as its /locations/:id page so the graphs merge
// rather than duplicate. Hours are read from the verified src/data/locations.ts.
const parseHoursRange = (range: string): { opens: string; closes: string } | null => {
  if (!range || /closed/i.test(range)) return null;
  const to24 = (t: string) => {
    const m = t.trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const min = m[2] ?? '00';
    const mer = m[3].toUpperCase();
    if (mer === 'PM' && h !== 12) h += 12;
    if (mer === 'AM' && h === 12) h = 0;
    return `${String(h).padStart(2, '0')}:${min}`;
  };
  const [a, b] = range.split(/\s*-\s*/);
  const opens = a && to24(a);
  const closes = b && to24(b);
  return opens && closes ? { opens, closes } : null;
};

export const getBranchesSchema = () => ({
  '@context': 'https://schema.org',
  '@graph': locations.map((location) => ({
    '@type': 'LocalBusiness',
    '@id': `${BASE_URL}/locations/${location.id}#business`,
    name: `Supreme Turf — ${location.name}`,
    image: `${BASE_URL}/apple-touch-icon.png`,
    url: `${BASE_URL}/locations/${location.id}`,
    telephone: `+61${location.phone.replace(/\s/g, '').replace(/^0/, '')}`,
    email: location.email,
    parentOrganization: { '@id': `${BASE_URL}/#organization` },
    address: {
      '@type': 'PostalAddress',
      streetAddress: location.address,
      addressLocality: location.city,
      addressRegion: location.state,
      postalCode: location.postcode,
      addressCountry: 'AU',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: location.coordinates.lat,
      longitude: location.coordinates.lng,
    },
    openingHoursSpecification: [
      { days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], range: location.hours.weekdays },
      { days: ['Saturday'], range: location.hours.saturday },
      { days: ['Sunday'], range: location.hours.sunday },
    ]
      .map(({ days, range }) => {
        const parsed = parseHoursRange(range);
        return parsed
          ? { '@type': 'OpeningHoursSpecification', dayOfWeek: days, opens: parsed.opens, closes: parsed.closes }
          : null;
      })
      .filter(Boolean),
    areaServed: { '@type': 'State', name: location.state },
    priceRange: '$$',
  })),
});


// Product Schema
export const getProductSchema = (product: {
  name: string;
  description: string;
  image: string;
  price: string;
  sku?: string;
  availability?: boolean;
  category?: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: product.name,
  description: product.description,
  image: product.image,
  sku: product.sku,
  category: product.category,
  brand: { '@type': 'Brand', name: 'Supreme Turf' },
  offers: {
    '@type': 'Offer',
    price: product.price,
    priceCurrency: 'AUD',
    availability: product.availability !== false
      ? 'https://schema.org/InStock'
      : 'https://schema.org/OutOfStock',
    seller: { '@id': `${BASE_URL}/#organization` },
  },
});

// Breadcrumb Schema
export const getBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `${BASE_URL}${item.url}`,
  })),
});

// FAQ Schema
export const getFAQSchema = (faqs: Array<{ question: string; answer: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

// Article Schema for blog posts
export const getArticleSchema = (article: {
  title: string;
  description: string;
  image: string;
  publishedAt: string;
  modifiedAt: string;
  authorName: string;
  slug: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: article.title,
  description: article.description,
  image: article.image,
  datePublished: article.publishedAt,
  dateModified: article.modifiedAt,
  author: {
    '@type': 'Organization',
    name: article.authorName,
  },
  publisher: {
    '@type': 'Organization',
    name: 'Supreme Turf',
    logo: {
      '@type': 'ImageObject',
      url: `${BASE_URL}/logo.png`,
    },
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${BASE_URL}/blog/${article.slug}`,
  },
});

// Website Search Schema
export const getWebsiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${BASE_URL}/#website`,
  name: 'Supreme Turf',
  url: BASE_URL,
  publisher: { '@id': `${BASE_URL}/#organization` },
  inLanguage: 'en-AU',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${BASE_URL}/shop?search={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
});
